import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import { ApiSave, ApiSearch } from '@assets/js/api/commonApi.js';

const CompFormVeCmptnApply = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [competition, setCompetition] = useState({});
    const [dvsnList, setDvsnList] = useState([]);
    const [wetclsList, setWetclsList] = useState([]);
    const [armTypeList, setArmTypeList] = useState([]);

    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalCompetitionApply';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['plyrNm', 'natNm', 'plyrGender', 'temno', 'email', 'dvsnCd', 'wetclsCd', 'armType'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_competition) {
            handleToggle();
            setCompetition(p_competition || {});
            setParams({
                ...p_competition,
                dvsnCd: [],
                wetclsCd: [],
                armType: []
            });

            // Load checkbox options
            loadDvsnList(p_competition?.mngCd, p_competition?.comptnCd);
            loadWetclsList(p_competition?.mngCd, p_competition?.comptnCd);
            loadArmTypeList();
        },
    }));

    /**********************
     * Define functions
    ***********************/
    // Load division list
    const loadDvsnList = (mngCd, comptnCd) => {
        ApiSearch('/common/code/dvsn/list', { grpCd: 'DVSNCD', mngCd: mngCd, comptnCd: comptnCd }).then(res => {
            if (res && res.success && res.data) {
                setDvsnList(res.data.dataList || []);
            }
        });
    }

    // Load weight class list
    const loadWetclsList = (mngCd, comptnCd) => {
        ApiSearch('/common/code/wetcls/list', { grpCd: 'WETCLSCD', mngCd: mngCd, comptnCd: comptnCd }).then(res => {
            if (res && res.success && res.data) {
                setWetclsList(res.data.dataList || []);
            }
        });
    }

    // Load arm type list
    const loadArmTypeList = () => {
        ApiSearch('/common/code/list', { grpCd: 'GRP005' }).then(res => {
            if (res && res.success && res.data) {
                setArmTypeList(res.data.dataList || []);
            }
        });
    }

    // Handle checkbox change for multi-select
    const handleMultiCheckboxChange = (field, value) => {
        const currentValues = params[field] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];
        setParams({ ...params, [field]: newValues });
    }

    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        // 모달이 닫힐 때 폼 초기화
        if (targetEle && targetEle.style.display === 'none') {
            setParams({});
            setCompetition({});
        }
    }

    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) {
            alert('필수 입력 항목을 확인해주세요.');
            return;
        }

        // 체크박스 필수 검증
        if (!params.dvsnCd || params.dvsnCd.length === 0) {
            alert('부문을 1개 이상 선택해주세요.');
            return;
        }

        if (!params.wetclsCd || params.wetclsCd.length === 0) {
            alert('체급을 1개 이상 선택해주세요.');
            return;
        }

        if (!params.armType || params.armType.length === 0) {
            alert('팔구분을 1개 이상 선택해주세요.');
            return;
        }

        // 동의 항목 검증
        if (params.injuryUseYn === 'Y' && params.injuryCstYn !== 'Y') {
            alert('부상 동의는 필수입니다.');
            return;
        }

        if (params.videoUseYn === 'Y' && params.videoCstYn !== 'Y') {
            alert('영상 촬영 동의는 필수입니다.');
            return;
        }

        // 저장 파라미터 구성
        const saveParams = {
            comptnCd: competition.comptnCd,
            mngCd: competition.mngCd,
            plyrNm: params.plyrNm,
            email: params.email,
            natNm: params.natNm,
            temno: params.temno,
            injuryCstYn: params.injuryCstYn || 'N',
            videoCstYn: params.videoCstYn || 'N',
            
            //배열값.
            dvsnCdArr: params.dvsnCd,
            wetclsCdArr: params.wetclsCd,
            armTypeArr: params.armType,
        };

        // 대회 신청정보 저장
        ApiSave('/ve/competition/apply/save', saveParams, {
            msg : {confirm : '대회 신청을 하시겠습니까?', message : '대회 신청이 완료 되었습니다.'}
        }).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
            }
        });
    }

    const onValChange = (e) => {
        const { id } = e.target;
        // Validate the specific input field
        validateForm(validateOpt, id);
    }

    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }

    const handleCheckboxChange = (e) => {
        const { id, checked } = e.target;
        setParams({ ...params, [id]: checked ? 'Y' : 'N' });
    }

    /****************************
     * Define form config
    *****************************/
    const formObj = {
        // 선수 기본 정보
        plyrNm: formAtt({ id: 'plyrNm', label: '성명', value: params['plyrNm'] || '', required: true, maxLength: 30, classCol: 'col-6', placeholder: "" }),
        natNm: formAtt({ id: 'natNm', label: '국적', value: params['natNm'] || '', required: true, maxLength: 30, classCol: 'col-6', placeholder: "" }),
        temno: formAtt({ id: 'temno', label: '연락처', value: params['temno'] || '', required: true, maxLength: 20, classCol: 'col-6', placeholder: "" }),
        email: formAtt({ id: 'email', label: '이메일', value: params['email'] || '', required: true, maxLength: 50, classCol: 'col-6', placeholder: "" }),

        // 대회 참가 정보
        dvsnCd: formAtt({ id: 'dvsnCd', label: '부문', value: params['dvsnCd'] || '', required: true, classCol: 'col-6' }),
        wetclsCd: formAtt({ id: 'wetclsCd', label: '체급', value: params['wetclsCd'] || '', required: true, classCol: 'col-6' }),
        armType: formAtt({ id: 'armType', label: '팔구분', value: params['armType'] || '', required: true, classCol: 'col-6' }),

        // 추가 정보
        injuryCstYn: formAtt({ id: 'injuryCstYn', label: '부상 동의', value: params['injuryCstYn'] || '' }),
        videoCstYn: formAtt({ id: 'videoCstYn', label: '영상촬영 동의', value: params['videoCstYn'] || '' }),
    }

    return (
        <>
            <div
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-3"><b>대회 신청</b></h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row">
                                <div className="col-lg-12">
                                    {/* 대회 정보 표시 */}
                                    <div className="bg-light p-3 rounded mb-4">
                                        <h6 className="mb-2"><b>{competition.comptnNm}</b></h6>
                                        <p className="mb-0 text-muted large">
                                            신청 기간: {competition.formStrtDate === competition.formDeadDate ? '~ ' + (competition.formStrtDate == null ? '' : competition.formStrtDate) : competition.formStrtDate + ' ~ ' + competition.formDeadDate}
                                        </p>
                                    </div>

                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* 선수 기본 정보 */}
                                            <div className="col-12">
                                                <h6 className="border-bottom pb-2 mb-3" style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    color: '#0d6efd',
                                                    borderColor: '#0d6efd !important',
                                                    borderWidth: '2px !important',
                                                    background: 'linear-gradient(135deg, #e7f1ff 0%, #f0f7ff 100%)',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '8px',
                                                    marginBottom: '1rem'
                                                }}>
                                                    <i className="bi bi-person-fill me-2"></i>
                                                    선수 정보
                                                </h6>
                                            </div>

                                            <CompFmtInput
                                                attrs={formObj.plyrNm}
                                                onChange={onInputChange}
                                                onBlur={onValChange}
                                            />
                                            <CompFmtInput
                                                attrs={formObj.email}
                                                onChange={onInputChange}
                                                onBlur={onValChange}
                                            />
                                            <CompFmtInput
                                                attrs={formObj.natNm}
                                                onChange={onInputChange}
                                                onBlur={onValChange}
                                            />
                                            <CompFmtInput
                                                attrs={formObj.temno}
                                                onChange={onInputChange}
                                                onBlur={onValChange}
                                            />
                                            
                                            {/* 대회 참가 정보 */}
                                            <div className="col-12">
                                                <h6 className="border-bottom pb-2 mb-3 mt-3" style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    color: '#198754',
                                                    borderColor: '#198754 !important',
                                                    borderWidth: '2px !important',
                                                    background: 'linear-gradient(135deg, #d1f4e0 0%, #e8f9f0 100%)',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '8px',
                                                    marginBottom: '1rem'
                                                }}>
                                                    <i className="bi bi-trophy-fill me-2"></i>
                                                    참가 정보
                                                </h6>
                                            </div>

                                            {/* 부문 체크박스 */}
                                            <div className="col-12">
                                                <label className="form-label fw-bold">
                                                    부문 <span className="text-danger">*</span>
                                                </label>
                                                <div className="p-3 border rounded mb-1" style={{ background: '#f8f9fa' }}>
                                                    <div className="row g-2">
                                                        {dvsnList.map((dvsn, index) => (
                                                            <div key={index} className="col-md-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`dvsnCd_${dvsn.code}`}
                                                                        checked={(params.dvsnCd || []).includes(dvsn.code)}
                                                                        onChange={() => handleMultiCheckboxChange('dvsnCd', dvsn.code)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`dvsnCd_${dvsn.code}`}>
                                                                        {dvsn.codeNm}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 체급 체크박스 */}
                                            <div className="col-12">
                                                <label className="form-label fw-bold">
                                                    체급 <span className="text-danger">*</span>
                                                </label>
                                                <div className="p-3 border rounded mb-1" style={{ background: '#f8f9fa' }}>
                                                    <div className="row g-2">
                                                        {wetclsList.map((wetcls, index) => (
                                                            <div key={index} className="col-md-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`wetclsCd_${wetcls.code}`}
                                                                        checked={(params.wetclsCd || []).includes(wetcls.code)}
                                                                        onChange={() => handleMultiCheckboxChange('wetclsCd', wetcls.code)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`wetclsCd_${wetcls.code}`}>
                                                                        {wetcls.codeNm}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 팔구분 체크박스 */}
                                            <div className="col-12">
                                                <label className="form-label fw-bold">
                                                    팔구분 <span className="text-danger">*</span>
                                                </label>
                                                <div className="p-3 border rounded" style={{ background: '#f8f9fa' }}>
                                                    <div className="row g-2">
                                                        {armTypeList.map((arm, index) => (
                                                            <div key={index} className="col-md-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`armType_${arm.code}`}
                                                                        checked={(params.armType || []).includes(arm.code)}
                                                                        onChange={() => handleMultiCheckboxChange('armType', arm.code)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`armType_${arm.code}`}>
                                                                        {arm.codeNm}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 동의 섹션 구분선 */}
                                            <div className="col-12 mt-4">
                                                <h6 className="border-bottom pb-2 mb-3" style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    color: '#fd7e14',
                                                    borderColor: '#fd7e14 !important',
                                                    borderWidth: '2px !important',
                                                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe8cc 100%)',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '8px',
                                                    marginBottom: '1rem'
                                                }}>
                                                    <i className="bi bi-check-circle-fill me-2"></i>
                                                    동의 사항
                                                </h6>
                                            </div>

                                            {/* 부상 동의 여부 */}
                                            {params.injuryUseYn === 'Y' && (
                                                <div className="col-12">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="injuryCstYn"
                                                            checked={'Y'}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="injuryCstYn">
                                                            대회 진행시 발생한 부상은 본인 책임이며 이점에 동의합니다.
                                                        </label>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 영상 촬영 여부 */}
                                            {params.videoUseYn === 'Y' && (
                                                <div className="col-12 mb-3">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="videoCstYn"
                                                            checked={'Y'}
                                                            onChange={handleCheckboxChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="videoCstYn">
                                                            영상 촬영에 동의합니다.
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-nowrap p-0">
                            <button
                                type="button"
                                className="btn btn-lg btn-link fs-6 text-decoration-none col-6 py-3 m-0 rounded-0 border-end"
                                onClick={handleToggle}
                                style={{
                                    color: '#6c757d',
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    borderTop: '1px solid #dee2e6'
                                }}
                            >
                                <strong>취소</strong>
                            </button>
                            <button
                                type="button"
                                className="btn btn-lg btn-link fs-6 text-decoration-none col-6 py-3 m-0 rounded-0"
                                onClick={onSave}
                                style={{
                                    color: '#ffffff',
                                    background: 'linear-gradient(135deg, #0d6efd 0%, #0056d2 100%)',
                                    borderTop: '1px solid #0d6efd'
                                }}
                            >
                                <strong>신청</strong>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CompFormVeCmptnApply;
